import React, { type ChangeEvent, type FormEvent, type FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl, authFetch, authService } from '../utils/api';

interface GenerateCodeFormData {
  grade: string;
  term: string;
  courseId: string;
  numberOfCodes: number;
}

interface Course {
  id: string;
  courseName: string;
  term: string;
  grade: string;
  description: string;
  price: number;
  active: boolean;
  imagePath: string;
  modificationDate: string;
}

interface GeneratedCode {
  code: string;
  grade: string;
  courseName: string;
}

const GenerateCode: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<GenerateCodeFormData>({
    grade: '',
    term: '',
    courseId: '',
    numberOfCodes: 1,
  });

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCourses = async (grade: string, term: string) => {
    if (!grade || !term) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        apiUrl(`Course/bygradeandterm?grade=${grade}&term=${term}&pageNumber=1&pageSize=100`),
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch courses');
      }
      
      const data = await response.json();
      if (data.success) {
        setCourses(data.data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch courses');
      }
    } catch (err) {
      setError('فشل في تحميل الباقات. يرجى المحاولة مرة أخرى.');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.grade && formData.term) {
      fetchCourses(formData.grade, formData.term);
    }
  }, [formData.grade, formData.term]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: name === 'numberOfCodes' ? parseInt(value, 10) || 0 : value,
      };

      // Reset courseId when grade or term changes
      if (name === 'grade' || name === 'term') {
        newData.courseId = '';
      }

      return newData;
    });
  };

  const downloadAsCSV = (codes: GeneratedCode[]) => {
    // Create CSV content with proper encoding for Arabic text
    const headers = ['Code', 'Grade', 'Course Name'];
    const rows = codes.map(code => [
      code.code,
      code.grade,
      `"${code.courseName.replace(/"/g, '""')}"` // Properly escape quotes and wrap in quotes for Arabic text
    ]);
    
    // Add BOM for proper UTF-8 encoding
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n'); // Using standard newline instead of escaped

    // Create blob with UTF-8 encoding
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Get the selected course name for the filename
    const selectedCourse = courses.find(course => course.id === formData.courseId);
    const filename = selectedCourse 
      ? `${selectedCourse.courseName}-codes-${new Date().toISOString().split('T')[0]}.csv`
      : `course-codes-${new Date().toISOString().split('T')[0]}.csv`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Make API call to generate codes with POST method
      const response = await authFetch(
        apiUrl(`CourseCode/GenerateCodeCourse?Courseid=${formData.courseId}&Count=${formData.numberOfCodes}`),
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate codes');
      }

      const result = await response.json();
      
      if (result.success) {
        // Download the generated codes as CSV
        downloadAsCSV(result.data);
        alert(result.message || 'تم إنشاء الرموز وتحميلها بنجاح!');
        navigate('/codes');
      } else {
        throw new Error(result.message || 'فشل في إنشاء الرموز');
      }
    } catch (err) {
      setError('فشل في إنشاء الرموز. يرجى المحاولة مرة أخرى.');
      console.error('Error generating codes:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6" dir="rtl">
      <h1 className="text-2xl font-bold mb-6">إنشاء رموز الدورة</h1>

      <div className="bg-white p-6 rounded-lg shadow max-w-2xl mx-auto">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الصف
              </label>
              <select
                name="grade"
                value={formData.grade}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={loading}
              >
                <option value="">اختر الصف</option>
                <option value="secondary1">الصف الأول الثانوي</option>
                <option value="secondary2">الصف الثاني الثانوي</option>
                <option value="secondary3">الصف الثالث الثانوي</option>
                <option value="literary3">أدبي - الصف الثالث الثانوي</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الفصل الدراسي
              </label>
              <select
                name="term"
                value={formData.term}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={loading}
              >
                <option value="">اختر الفصل الدراسي</option>
                <option value="first">الفصل الأول</option>
                <option value="second">الفصل الثاني</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الدورة
              </label>
              <select
                name="courseId"
                value={formData.courseId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={!formData.grade || !formData.term || loading}
              >
                <option value="">
                  {loading 
                    ? 'جاري تحميل الباقات...' 
                    : error 
                    ? 'خطأ في تحميل الباقات' 
                    : 'اختر الدورة'
                  }
                </option>
                {courses.map((course: Course) => (
                  <option key={course.id} value={course.id}>
                    {course.courseName}
                  </option>
                ))}
              </select>
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                عدد الرموز
              </label>
              <input
                type="number"
                name="numberOfCodes"
                value={formData.numberOfCodes}
                onChange={handleInputChange}
                min="1"
                max="501"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={loading}
              />
            </div>

            <div className="flex justify-end space-x-reverse space-x-4">
              <button
                type="button"
                onClick={() => navigate('/codes')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 bg-primary text-white rounded-lg transition-colors ${
                  loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/90'
                }`}
              >
                {loading ? 'جاري الإنشاء...' : 'إنشاء'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenerateCode; 